export type InterviewPeriod = 'all_day' | 'morning' | 'afternoon'

export const INTERVIEW_PERIOD_MORNING_TIME = '08:00'
export const INTERVIEW_PERIOD_AFTERNOON_TIME = '14:00'
export const INTERVIEW_PERIOD_ALL_DAY_TIME = '00:00'

const TIME_ONLY = /^(\d{1,2}):(\d{2})(?::\d{2})?$/
const DATETIME_LOCAL = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/

export function interviewHourToPeriod(hour: number): InterviewPeriod {
  if (!Number.isInteger(hour) || hour < 0 || hour > 23 || hour === 0) return 'all_day'
  return hour < 12 ? 'morning' : 'afternoon'
}

function parseHour(value?: string | null): number | null {
  const trimmed = value?.trim()
  if (!trimmed) return null
  const match = TIME_ONLY.exec(trimmed)
  if (!match) return null
  const hour = Number(match[1])
  const minute = Number(match[2])
  if (!Number.isInteger(hour) || !Number.isInteger(minute) || hour > 23 || minute > 59) {
    return null
  }
  return hour
}

export function interviewTimeToPeriod(time?: string | null): InterviewPeriod {
  const hour = parseHour(time)
  if (hour == null) return 'all_day'
  return interviewHourToPeriod(hour)
}

export function interviewPeriodToTime(period: InterviewPeriod | ''): string {
  if (period === 'morning') return INTERVIEW_PERIOD_MORNING_TIME
  if (period === 'afternoon') return INTERVIEW_PERIOD_AFTERNOON_TIME
  return INTERVIEW_PERIOD_ALL_DAY_TIME
}

export function interviewFormLocalDate(value: string): string {
  const trimmed = value.trim()
  const local = DATETIME_LOCAL.exec(trimmed)
  if (local) return local[1]
  return /^\d{4}-\d{2}-\d{2}$/.test(trimmed) ? trimmed : ''
}

export function interviewFormLocalTime(value: string): string {
  return DATETIME_LOCAL.exec(value.trim())?.[2] ?? INTERVIEW_PERIOD_ALL_DAY_TIME
}

export function interviewFormLocalFromDateAndTime(date: string, time: string): string {
  const day = date.trim()
  if (!day) return ''
  return `${day}T${time.trim() || INTERVIEW_PERIOD_ALL_DAY_TIME}`
}

/** Canonical date + period clock (00:00 / 08:00 / 14:00) from a form-local datetime. */
export function normalizeInterviewFormLocal(value: string): string {
  const date = interviewFormLocalDate(value)
  if (!date) return ''
  return interviewFormLocalFromDateAndTime(
    date,
    interviewPeriodToTime(interviewTimeToPeriod(interviewFormLocalTime(value))),
  )
}

export function interviewPeriodLabel(
  time: string | null | undefined,
  translate: (key: string) => string,
): string {
  const period = interviewTimeToPeriod(time)
  if (period === 'morning') return translate('applications.interviewMorning')
  if (period === 'afternoon') return translate('applications.interviewAfternoon')
  return translate('applications.interviewAllDay')
}

export function formatInterviewDatePeriod(
  date: string | null | undefined,
  time: string | null | undefined,
  formatDateFn: (value?: string | null) => string,
  translate: (key: string) => string,
): string {
  const empty = translate('common.dash')
  if (!date?.trim()) return empty
  const dateLabel = formatDateFn(date)
  const period = interviewPeriodLabel(time, translate)
  return period ? `${dateLabel} · ${period}` : dateLabel
}
